import * as React from 'react';

import { DetentionSortOption } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  AsyncBoundary,
  Button,
  Content,
  DataGrid,
  DataGridColumn,
  Footer,
  HeaderIcons,
  LocalizedDate,
  Pagination,
  SearchField,
  Toolbar,
  type PaginationProps,
} from '@rematter/ui-web';
import { LucidePlus } from 'lucide-react';

import { DetentionDialog } from './DetentionDialog';

import { useDetentionsListQuery } from '~/modules/transportation/hooks/queries/useDetentionsListQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { DetentionRecord } from '~/modules/transportation/types';

const COLUMN_TO_SORT_MAP: Record<string, { asc: string; desc: string }> = {
  startDate: {
    asc: 'StartDateAsc',
    desc: 'StartDateDesc',
  },
  createdAt: {
    asc: 'CreatedAtAsc',
    desc: 'CreatedAtDesc',
  },
};

export const DetentionsListContent: React.FC = () => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [offset, setOffset] = React.useState<number>(0);
  const [limit, setLimit] = React.useState<number>(50);
  const [sorting, setSorting] = React.useState<DetentionSortOption[]>([
    'StartDateDesc',
  ]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editDetention, setEditDetention] =
    React.useState<DetentionRecord | null>(null);

  const { data, error, isLoading, isFetching, refetch } =
    useDetentionsListQuery({
      filter: {},
      sorting,
      pagination: { limit, offset },
    });

  const detentions: DetentionRecord[] = React.useMemo(
    () => data?.detentions ?? [],
    [data?.detentions],
  );
  const total = data?.total ?? 0;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleEdit = React.useCallback((detention: DetentionRecord) => {
    setEditDetention(detention);
    setIsDialogOpen(true);
  }, []);

  const handleCreate = React.useCallback(() => {
    setEditDetention(null);
    setIsDialogOpen(true);
  }, []);

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setSorting(['StartDateDesc']);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setSorting([sortOption as DetentionSortOption]);
        }
      }
      setOffset(0);
    },
    [],
  );

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: DetentionRecord }) => {
      handleEdit(row);
    },
    [handleEdit],
  );

  const columns: DataGridColumn<DetentionRecord>[] = React.useMemo(
    () => [
      {
        key: 'carrierName',
        header: t.detentions.table.columns.carrier,
        width: 200,
        icon: HeaderIcons.Text,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center font-medium">
            {row.carrierName ?? '-'}
          </div>
        ),
      },
      {
        key: 'locationName',
        header: t.detentions.table.columns.location,
        width: 200,
        icon: HeaderIcons.Text,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="truncate text-neutral/70">
            {row.locationName ?? '-'}
          </div>
        ),
      },
      {
        key: 'startDate',
        header: t.detentions.table.columns.startDate,
        sortable: true,
        width: 140,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.startDate} />
          </div>
        ),
      },
      {
        key: 'endDate',
        header: t.detentions.table.columns.endDate,
        width: 140,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            {row.endDate ? <LocalizedDate date={row.endDate} /> : '-'}
          </div>
        ),
      },
      {
        key: 'freeMinutes',
        header: t.detentions.table.columns.freeMinutes,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <div className="text-right">{row.freeMinutes}</div>,
      },
      {
        key: 'ratePerMinute',
        header: t.detentions.table.columns.ratePerMinute,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-right">${row.ratePerMinute}</div>
        ),
      },
      {
        key: 'createdAt',
        header: t.detentions.table.columns.createdAt,
        sortable: true,
        width: 140,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.detentions.emptyState.title,
      description: <span>{t.detentions.emptyState.description}</span>,
      centered: true,
    }),
    [t],
  );

  return (
    <AsyncBoundary
      loading={isLoading || isFetching}
      error={error}
      className="@DetentionsListContent"
    >
      <Toolbar className="flex justify-between">
        <SearchField
          value={searchTerm}
          onChange={handleSearch}
          placeholder={t.detentions.toolbar.search}
          className="w-[200px] shrink-0"
        />
        <Button color="primary" variant="solid" onPress={handleCreate}>
          <LucidePlus size={16} />
          {t.detentions.toolbar.create}
        </Button>
      </Toolbar>

      <Content className="h-full">
        <DataGrid<DetentionRecord>
          isLoading={isLoading || isFetching}
          columns={columns}
          dataSource={detentions}
          rowKey="id"
          aria-label={t.detentions.table.aria}
          onColumnSort={handleColumnSort}
          columnVisibilityState={columnVisibilityState}
          className="w-full text-sm"
          fullWidth
          stickyHeader
          emptyPanelProps={emptyPanelProps}
        />
      </Content>

      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}

      <DetentionDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditDetention(null);
        }}
        onSuccess={() => {
          setIsDialogOpen(false);
          setEditDetention(null);
          refetch();
        }}
        detention={editDetention ?? undefined}
      />
    </AsyncBoundary>
  );
};

export const DetentionsListView: React.FC = () => (
  <TransportationRootLayout className="@DetentionsListView">
    <DetentionsListContent />
  </TransportationRootLayout>
);
